import { Calendar, Pencil, Play, User } from "lucide-react";
import * as React from "react";
import { Link, useParams } from "react-router";

import { useAuth } from "@/providers";
import {
  FormPageHeading,
  PageBreadcrumbs,
  PageLayout,
  UserLink,
} from "@/shared/components";
import { FormattedDateTime } from "@/shared/datetime";
import { type AgentRecord } from "@/shared/domain/agents";
import { useAgent } from "@/shared/domain/agents/hooks";
import { ButtonLink, Spinner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

import { parseSavedAgent } from "../Agents/buildRequestTemplate";

import styles from "./AgentDetail.module.css";
import { DangerZone } from "./sections/DangerZone";
import { GoalsSection } from "./sections/GoalsSection";
import { RecentThreadsSection } from "./sections/RecentThreadsSection";
import { SettingsSection } from "./sections/SettingsSection";
import { SkillsSection } from "./sections/SkillsSection";

/**
 * One saved agent, as every link to an agent shows it: what it works on, what
 * it may use, how it runs, and the ways in to running or changing it.
 *
 * Nothing here edits: each section links to the form, opened on that section.
 * The one write is deleting the agent, from the danger zone at the bottom.
 */
export function AgentDetail() {
  const { agentId } = useParams<{ agentId: string }>();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;
  const agentQuery = useAgent(agentId, { orgId });
  const agent = agentQuery.data;
  // The org switch offers switching into the org that owns what is on screen.
  useRegisterViewingContext("agent", { orgId: agent?.org_id });

  const name = agent?.name ?? "Agent";
  const breadcrumbs = (
    <PageBreadcrumbs>
      <Link to={Goto.route.Agents}>Agents</Link>
      <span>{name}</span>
    </PageBreadcrumbs>
  );

  // Gated on `data === undefined` rather than `isLoading`: TanStack reports
  // `isLoading: false` for a disabled query, and `useAgent` is disabled until
  // `orgId` resolves, so that window would otherwise render an empty page.
  let body: React.ReactNode;
  if (agentQuery.error !== null) {
    body = (
      <p className={styles.error} data-testid="agent-detail-error" role="alert">
        Failed to load agent. {agentQuery.error.message}
      </p>
    );
  } else if (agent === undefined) {
    body = (
      <p className={styles.loading} data-testid="agent-detail-loading">
        <Spinner aria-hidden />
        Loading agent&hellip;
      </p>
    );
  } else {
    body = <Overview agent={agent} />;
  }

  return (
    <PageLayout title={`Roboto - ${name}`} headerContentLeft={breadcrumbs}>
      <div className={styles.page} data-testid="agent-detail-page">
        {body}
      </div>
    </PageLayout>
  );
}

function Overview({ agent }: { agent: AgentRecord }) {
  const form = React.useMemo(() => parseSavedAgent(agent), [agent]);

  return (
    <>
      <div className={styles.header}>
        <div className={styles.heading}>
          <FormPageHeading
            title={agent.name}
            subtitle={agent.description ?? undefined}
          />
          <div
            className={styles.metadataRow}
            data-testid="agent-detail-created"
          >
            <User aria-hidden className={styles.metadataIcon} />
            <span className={styles.metadataLabel}>Created By:</span>
            <span className={styles.metadataText}>
              <UserLink userId={agent.created_by} />
            </span>
            <span className={styles.metadataDot}>&middot;</span>
            <Calendar aria-hidden className={styles.metadataIcon} />
            <span className={styles.metadataLabel}>Created:</span>
            <span className={styles.metadataText}>
              <FormattedDateTime value={agent.created} time="none" />
            </span>
          </div>
        </div>
        <div className={styles.actions}>
          <ButtonLink
            variant="secondary"
            icon={<Pencil aria-hidden />}
            href={Goto.createEditAgentRoute(agent.agent_id)}
            data-testid="agent-edit"
          >
            Edit
          </ButtonLink>
          <ButtonLink
            variant="primary"
            icon={<Play aria-hidden />}
            href={Goto.createAgentLaunchRoute(agent.agent_id)}
            data-testid="agent-launch"
          >
            Launch agent
          </ButtonLink>
        </div>
      </div>

      <RecentThreadsSection agentId={agent.agent_id} orgId={agent.org_id} />
      <GoalsSection
        agentId={agent.agent_id}
        goals={form.goals}
        kickoffMessage={form.kickoffMessage}
        variables={agent.variables}
      />
      <SkillsSection agentId={agent.agent_id} skills={form.skills} />
      <SettingsSection
        agentId={agent.agent_id}
        modelProfile={form.modelProfile}
      />
      <DangerZone
        agentId={agent.agent_id}
        orgId={agent.org_id}
        agentName={agent.name}
      />
    </>
  );
}
